import React, { CSSProperties, useEffect, useState } from 'react';
import ITabs from '../../../models/tabs/ITabs';
import { getHeapAttributes } from '../../../utils/heap/heap.helper';
import styles from './tabs.module.scss';
import { TabAlignType } from '../../../constants/tabs-align-type';
import { TabStyleType } from '../../../constants/tabs-style-type';

type Props = {
  tabProps: ITabs[];
  tabStyle?: TabStyleType;
  tabAlign?: TabAlignType;
  customClass?: string;
  onChange?: (currentTab: string) => void;
};

const getTabActionClassNames = (
  currentTab: string,
  tab: ITabs,
  tabAlign: TabAlignType,
  tabStyle: TabStyleType,
) => {
  let classNames = [styles['tabs-action']];

  if (currentTab === tab.id) {
    classNames.push(styles['active']);
  }

  if (tabAlign === TabAlignType.Vertical) {
    classNames.push(styles['vertical']);
  } else {
    classNames.push(styles['horizontal']);
  }

  if (tabStyle === TabStyleType.Filled) {
    classNames.push(styles['filled']);
  }

  return classNames.join(' ');
};

export default function Tabs({ tabProps, tabStyle, tabAlign, customClass, onChange }: Props) {
  const [currentTab, setCurrentTab] = useState(tabProps[0]?.id);

  useEffect(() => {
    if (onChange) onChange(currentTab);
  }, [currentTab, onChange]);

  const focusSelectedTab = (id: string) => {
    document.getElementById(id)?.focus();
  };

  const previousTab = (tab: ITabs) => {
    const index = tabProps.indexOf(tab);
    if (index > 0) {
      const prevTabId = tabProps[index - 1].id;
      setCurrentTab(prevTabId);
      focusSelectedTab(prevTabId);
    }
  };

  const nextTab = (tab: ITabs) => {
    const index = tabProps.indexOf(tab);
    if (index < tabProps.length - 1) {
      const nextTabId = tabProps[index + 1].id;
      setCurrentTab(nextTabId);
      focusSelectedTab(nextTabId);
    }
  };

  const handleClick = (e) => {
    e.preventDefault();
    setCurrentTab(e.target.id);
  };

  const handleKeyup = (e, tab: ITabs) => {
    e.preventDefault();
    if (e.which === 13)
      setCurrentTab(e.target.id); // Enter
    else if (e.which === 37 || e.which === 38)
      previousTab(tab); // ArrowLeft, ArrowUp
    else if (e.which === 39 || e.which === 40) nextTab(tab); // ArrowRight, ArrowDown
  };

  return (
    <div
      className={`${styles['tabs-block']} ${tabAlign && styles[`tabs-${tabAlign}`]} ${styles[customClass]}`}
    >
      <div className={styles['tabs-list']} role='tablist'>
        {tabProps.map((tab) => (
          <button
            type='button'
            key={tab.id}
            id={tab.id}
            onClick={handleClick}
            onKeyUp={(e) => handleKeyup(e, tab)}
            role='tab'
            aria-selected={currentTab === tab.id}
            aria-controls={`tabpanel-${tab.id}`}
            tabIndex={currentTab === tab.id ? 0 : -1}
            className={getTabActionClassNames(currentTab, tab, tabAlign, tabStyle)}
            style={
              {
                '--tabs-copy-color': tab.copyColor,
                '--tabs-active-color': tab.activeColor,
              } as CSSProperties
            }
            {...getHeapAttributes(tab.heapIdentifier, { tabItem: tab.heading })}
          >
            {tab.heading}
          </button>
        ))}
      </div>
      {tabProps.map((tab) => (
        <div
          key={tab.id}
          id={`tabpanel-${tab.id}`}
          role='tabpanel'
          aria-labelledby={tab.id}
          className={`${styles['tabs-panel']} ${currentTab === tab.id ? styles['active'] : ''}`}
        >
          {tab.content}
        </div>
      ))}
    </div>
  );
}
